import React, { useState, useEffect, useCallback } from 'react';
import {
  Breadcrumb, Card, Table, Tag, Typography, Spin, Empty,
  Button, Row, Col, Statistic, Popconfirm, message, Space, Tooltip, Modal,
} from 'antd';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { SyncOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined, EyeOutlined, BarChartOutlined } from '@ant-design/icons';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getPlotsApi, getCropsByPlotIdApi, deletePlotApi, createCropApi, updateCropApi, deleteCropApi, changeCropStatusApi } from '../../api/crops';
import { AREA_UNITS, CROP_NAME_IMAGE } from '../../utils/constants';
import { formatCurrency, formatDate } from '../../utils/formatters';
import dayjs from 'dayjs';
import PlotMiniMap from './PlotMiniMap';
import iconPlot from '../../icons/plot.png';
import iconFarm from '../../icons/farm.png';
import iconCrop from '../../icons/crop.png';
import AddPlotModal from './AddPlotModal';
import CropModal from '../Crops/CropModal';
import HarvestModal from '../../components/HarvestModal';

const { Title, Text } = Typography;

function CropReportModal({ open, onClose, crops }) {
  const chartData = Object.values(
    crops.reduce((acc, c) => {
      const name = c.crop_name || c.crop || 'Unknown';
      const profit = (c.vechan || 0) - (c.kharch || 0);
      if (!acc[name]) acc[name] = { name, profit: 0 };
      acc[name].profit += profit;
      return acc;
    }, {})
  ).sort((a, b) => b.profit - a.profit);

  return (
    <Modal title="Crop Profit Report" open={open} onCancel={onClose} footer={null} width={600}>
      {chartData.length === 0 ? (
        <Empty description="No crop data" />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-30} textAnchor="end" interval={0} />
            <YAxis tickFormatter={(v) => `₹${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} tick={{ fontSize: 11 }} />
            <ChartTooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Profit']} />
            <ReferenceLine y={0} stroke="#ccc" strokeDasharray="4 4" />
            <Bar dataKey="profit" radius={[4, 4, 0, 0]} maxBarSize={48}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.profit >= 0 ? '#52c41a' : '#ff4d4f'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Modal>
  );
}

export default function PlotDetail() {
  const { plotId } = useParams();
  const navigate = useNavigate();
  const [plot, setPlot] = useState(null);
  const [crops, setCrops] = useState([]);
  const [loadingPlot, setLoadingPlot] = useState(true);
  const [loadingCrops, setLoadingCrops] = useState(false);
  const [editPlotOpen, setEditPlotOpen] = useState(false);
  const [addCropOpen, setAddCropOpen] = useState(false);
  const [addCropLoading, setAddCropLoading] = useState(false);
  const [editingCrop, setEditingCrop] = useState(null);
  const [editCropLoading, setEditCropLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [harvestingCrop, setHarvestingCrop] = useState(null);

  // Load the plot details first
  useEffect(() => {
    setLoadingPlot(true);
    getPlotsApi()
      .then((res) => {
        const list = res.data?.data || [];
        const found = list.find((p) => String(p.id) === String(plotId));
        setPlot(found || null);
      })
      .catch(() => {})
      .finally(() => setLoadingPlot(false));
  }, [plotId]);

  // Load crops for this specific plot
  const fetchCrops = useCallback(async () => {
    setLoadingCrops(true);
    try {
      const res = await getCropsByPlotIdApi(plotId);
      const raw = res.data?.data ?? res.data;
      const list = Array.isArray(raw) ? raw : [];
      setCrops(list.sort((a, b) => {
        const aActive = a.status === 1 || a.status === '1' ? 0 : 1;
        const bActive = b.status === 1 || b.status === '1' ? 0 : 1;
        return aActive - bActive;
      }));
    } catch {
      setCrops([]);
    } finally {
      setLoadingCrops(false);
    }
  }, [plotId]);

  // fetch crops as soon as plotId is available — no need to wait for plot details
  useEffect(() => { fetchCrops(); }, [fetchCrops]);

  const totalExpense = crops.reduce((s, c) => s + (c.kharch || 0), 0);
  const totalSelling = crops.reduce((s, c) => s + (c.vechan || 0), 0);
  const profit = totalSelling - totalExpense;

  const columns = [
    {
      title: 'Crop',
      dataIndex: 'crop_name',
      key: 'crop_name',
      render: (name, record) => (
        <Button
          type="link"
          style={{ padding: 0, color: '#1a6b2a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}
          onClick={() => navigate(`/farms/${record.farm_id}/farm/${record.id}/expenses`)}
        >
          {record.image
            ? <img src={CROP_NAME_IMAGE(record.image)} alt={name} style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
            : <span>🌱</span>}
          <div style={{ textAlign: 'left' }}>
            <div>{name || record.crop}</div>
            {record.verity && <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{record.verity}</div>}
          </div>
        </Button>
      ),
    },
    {
      title: 'Area',
      key: 'area',
      render: (_, r) => r.area ? `${r.area} ${AREA_UNITS[r.area_unit] || r.area_unit}` : '—',
    },
    {
      title: 'Sowing Date',
      dataIndex: 'crop_date',
      render: (v, record) => {
        if (!v) return '—';
        const today = dayjs();
        const hd = record.harvest_date ? dayjs(record.harvest_date) : null;
        const endDate = hd && !hd.isAfter(today, 'day') ? hd : today;
        const days = endDate.diff(dayjs(v), 'day');
        return (
          <div>
            <div>{formatDate(v)}</div>
            <div style={{ fontSize: 12, color: '#1a6b2a', marginTop: 2, fontWeight: 500 }}>crop age: {days} days</div>
          </div>
        );
      },
    },
    {
      title: 'Harvest Date',
      dataIndex: 'harvest_date',
      render: formatDate,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s, record) => {
        const isActive = s === 1 || s === '1';
        const noHarvestDate = !record.harvest_date;
        const harvestDue = record.harvest_date && !dayjs().isBefore(dayjs(record.harvest_date), 'day');
        const showMarkBtn = isActive && (noHarvestDate || harvestDue);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
            <Tag
              color={isActive ? 'green' : 'orange'}
              icon={isActive ? <SyncOutlined spin /> : <CheckCircleOutlined />}
            >
              {isActive ? 'Active' : 'Harvested'}
            </Tag>
            {showMarkBtn && (
              <Button
                size="small"
                style={{ fontSize: 11, borderColor: '#d46b08', color: '#d46b08', height: 22, padding: '0 6px' }}
                onClick={() => setHarvestingCrop(record)}
              >
                Mark Harvested
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: 'Expense',
      dataIndex: 'kharch',
      render: (v) => <span style={{ color: '#fa8c16' }}>{formatCurrency(v)}</span>,
    },
    {
      title: 'Selling',
      dataIndex: 'vechan',
      render: (v) => <span style={{ color: '#52c41a' }}>{formatCurrency(v)}</span>,
    },
    {
      title: 'Profit',
      key: 'profit',
      render: (_, r) => {
        const p = (r.vechan || 0) - (r.kharch || 0);
        return (
          <span style={{ color: p >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
            {formatCurrency(p)}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
              onClick={() => navigate(`/farms/${record.farm_id}/farm/${record.id}/expenses`)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setEditingCrop(record); }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title="Delete this crop?" onConfirm={() => handleDeleteCrop(record)} okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loadingPlot) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (!plot) {
    return (
      <Empty description="Plot not found">
        <Link to="/plots"><Button type="primary">Back to Plots</Button></Link>
      </Empty>
    );
  }

  const handleDelete = async () => {
    try {
      await deletePlotApi(plotId);
      message.success('Plot deleted');
      navigate('/plots');
    } catch {
      message.error('Failed to delete plot');
    }
  };

  const handleEditCrop = async (values) => {
    setEditCropLoading(true);
    try {
      const payload = { ...values, cropId: editingCrop.id };
      if (!payload.harvest_date) delete payload.harvest_date;
      await updateCropApi(payload);
      // updateCrop API doesn't save status — call changeCropStatus separately if it changed
      if (payload.status !== undefined && payload.status !== editingCrop.status) {
        await changeCropStatusApi({ crop_id: editingCrop.id, status: payload.status });
      }
      message.success('Crop updated!');
      setEditingCrop(null);
      fetchCrops();
    } catch {
      message.error('Failed to update crop');
    } finally {
      setEditCropLoading(false);
    }
  };

  const handleDeleteCrop = async (crop) => {
    try {
      await deleteCropApi({ crop_id: crop.id });
      message.success('Crop deleted');
      fetchCrops();
    } catch {
      message.error('Failed to delete crop');
    }
  };

  const handleAddCrop = async (values) => {
    setAddCropLoading(true);
    try {
      await createCropApi(values);
      message.success('Crop added!');
      setAddCropOpen(false);
      fetchCrops();
    } catch {
      message.error('Failed to add crop');
    } finally {
      setAddCropLoading(false);
    }
  };

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/plots">Plots</Link> },
          { title: plot.name },
        ]}
      />

      {/* Plot header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          <PlotMiniMap path={plot.path} activeCrop={crops.some((c) => c.status === 1 || c.status === '1')} />
          <div>
            <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 20, height: 20, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconPlot}) no-repeat center / contain`, mask: `url(${iconPlot}) no-repeat center / contain`, flexShrink: 0 }} />
              {plot.name}
            </Title>
            {plot.farm_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#888', WebkitMask: `url(${iconFarm}) no-repeat center / contain`, mask: `url(${iconFarm}) no-repeat center / contain`, flexShrink: 0 }} />
                <Text type="secondary">{plot.farm_name}</Text>
              </div>
            )}
            <div style={{ marginTop: 4 }}>
              <Text>
                {plot.seted_area} {AREA_UNITS[plot.seted_area_unit] || plot.seted_area_unit}
              </Text>
            </div>
          </div>
        </div>
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={() => setEditPlotOpen(true)}>
            Edit Plot
          </Button>
          <Tooltip title={crops.length > 0 ? 'Remove all crops before deleting this plot' : 'Delete this plot'}>
            <span>
              <Popconfirm
                title="Delete this plot?"
                description="This will permanently remove the plot and cannot be undone."
                onConfirm={handleDelete}
                okButtonProps={{ danger: true }}
                okText="Delete"
                disabled={crops.length > 0}
              >
                <Button danger icon={<DeleteOutlined />} disabled={crops.length > 0}>Delete Plot</Button>
              </Popconfirm>
            </span>
          </Tooltip>
        </Space>
      </div>

      {/* Totals */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: 'Total Expense', value: totalExpense, color: '#fa8c16', bg: '#fff7e6' },
          { label: 'Total Selling', value: totalSelling, color: '#52c41a', bg: '#f6ffed' },
          { label: profit >= 0 ? 'Net Profit' : 'Net Loss', value: profit, color: profit >= 0 ? '#52c41a' : '#ff4d4f', bg: profit >= 0 ? '#f6ffed' : '#fff1f0' },
        ].map((item) => (
          <Col xs={24} sm={8} key={item.label}>
            <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
              <Statistic
                title={item.label}
                value={formatCurrency(Math.abs(item.value))}
                valueStyle={{ color: item.color, fontSize: 18, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Crops table */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 16, height: 16, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconCrop}) no-repeat center / contain`, mask: `url(${iconCrop}) no-repeat center / contain`, flexShrink: 0 }} />
            All Crops <Text type="secondary" style={{ fontSize: 13 }}>({crops.length})</Text>
          </div>
        }
        extra={
          <Space>
            <Button icon={<BarChartOutlined />} size="small" onClick={() => setReportOpen(true)}>
              Report
            </Button>
            <Button icon={<PlusOutlined />} type="primary" size="small" style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }} onClick={() => setAddCropOpen(true)}>
              Add Crop
            </Button>
          </Space>
        }
        style={{ borderRadius: 10 }}
      >
        {loadingCrops ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : crops.length === 0 ? (
          <Empty description="No crops found for this plot" />
        ) : (
          <Table
            dataSource={crops}
            columns={columns}
            rowKey="id"
            scroll={{ x: 900 }}
            onRow={(record) => ({
              onClick: () => navigate(`/farms/${record.farm_id}/farm/${record.id}/expenses`),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      <CropReportModal open={reportOpen} onClose={() => setReportOpen(false)} crops={crops} />

      <HarvestModal
        open={!!harvestingCrop}
        crop={harvestingCrop}
        onClose={() => setHarvestingCrop(null)}
        onSave={async (payload) => {
          await changeCropStatusApi({
            crop_id: harvestingCrop.id,
            status: payload.status,
            harvest_date: payload.harvest_date,
          });
          message.success('Crop updated');
          setHarvestingCrop(null);
          fetchCrops();
        }}
      />

      <AddPlotModal
        open={editPlotOpen}
        onClose={() => setEditPlotOpen(false)}
        onSuccess={() => { setEditPlotOpen(false); }}
        initialPlot={plot}
      />

      <CropModal
        open={addCropOpen}
        onClose={() => setAddCropOpen(false)}
        onSubmit={handleAddCrop}
        initialValues={null}
        farmId={plot?.farm_id}
        selectedPlotId={plot?.id}
        selectedPlot={plot}
        plots={plot ? [plot] : []}
        loading={addCropLoading}
      />

      <CropModal
        open={!!editingCrop}
        onClose={() => setEditingCrop(null)}
        onSubmit={handleEditCrop}
        initialValues={editingCrop}
        farmId={plot?.farm_id}
        selectedPlotId={plot?.id}
        selectedPlot={plot}
        plots={plot ? [plot] : []}
        loading={editCropLoading}
      />
    </div>
  );
}
